<template>
  <form
    class="vehicle-finder-form__form form-group"
    @submit.prevent="handleSubmit"
  >
    <div class="vehicle-finder-form__types form-group__field">
      <vehicle-type
        form="vrm"
        :namespace="localNamespace"
        :parent-type="type"
        :tooltip-active="typeTooltipActive"
        @update-selected-type="handleTypeUpdate"
        @update-tooltip="handleTooltipUpdate"
      />
    </div>

    <div class="vehicle-finder-form__registration">
      <div class="vehicle-finder-form__input form-group__field">
        <label
          class="form-group__label text-body-xs-bold"
          :for="`${localNamespace}-registration`"
        >
          <span v-text="$string('vehicle_finder.vrm.registration_number')" />

          <abbr
            :title="$string('general.required.label')"
            v-text="$string('general.required.symbol')"
          />
        </label>

        <input
          :id="`${localNamespace}-registration`"
          v-model="registration"
          :class="{ [$class('error')]: error.input === `${localNamespace}-registration` }"
          autocomplete="off"
          :disabled="!type"
          :maxlength="$variable('vehicleFinder.vrmMaxCharacters') || null"
          :placeholder="$string('vehicle_finder.vrm.registration_number')"
          required
          type="text"
          :aria-describedby="error.input === `${localNamespace}-registration`
            ? `${localNamespace}-error`
            : null
          "
          :aria-invalid="error.input === `${localNamespace}-registration`"
          @input="() => registration = registration.toUpperCase()"
        >
      </div>

      <div
        v-if="$variable('vehicleFinder.vrmStates')?.length"
        class="vehicle-finder-form__input form-group__field"
      >
        <label
          class="form-group__label text-body-xs-bold"
          :for="`${localNamespace}-state`"
        >
          <span v-text="$string('vehicle_finder.vrm.state')" />

          <abbr
            :title="$string('general.required.label')"
            v-text="$string('general.required.symbol')"
          />
        </label>

        <select
          :id="`${localNamespace}-state`"
          v-model="state"
          :class="{ [$class('error')]: error.input === `${localNamespace}-state` }"
          autocomplete="off"
          :disabled="!registration"
          required
          :aria-describedby="error.input === `${localNamespace}-state`
            ? `${localNamespace}-error`
            : null
          "
          :aria-invalid="error.input === `${localNamespace}-state`"
        >
          <option
            hidden
            selected
            value=""
            v-text="$string('vehicle_finder.vrm.state')"
          />

          <option
            v-for="(stateOption) of $variable('vehicleFinder.vrmStates')"
            :key="stateOption.code"
            :value="stateOption.code"
            v-text="stateOption.name"
          />
        </select>
      </div>
    </div>

    <p
      v-show="error.message"
      :id="`${localNamespace}-error`"
      class="vehicle-finder-form__error text-body-s-regular"
      role="status"
      v-text="error.message"
    />

    <div class="vehicle-finder-form__submit form-group__footer">
      <btn
        :label="$string('vehicle_finder.cta_labels.search')"
        :show-state-icon="showSubmitStateIcon"
        type="submit"
      >
        <template #stateIcon>
          <component :is="submitStateIcon" />
        </template>
      </btn>
    </div>
  </form>
</template>

<script>

/**
 * Vue: VRM form (vehicle-finder-form)
 * -----------------------------------------------------------------------------
 * Form to run VRM search.
 *
 * @param {Boolean} [forwarding] - Page is being forwarded to results URL.
 * @param {Boolean} isActive - Tab is active.
 * @param {String} namespace - Unique namespace for form elements.
 * @param {Object} overlay - Overlay API.
 * @param {Boolean|Boolean} results - Product results for found vehicle.
 *
 * @emits vehicleResponse - Emits pre-formatted vehicle object response.
 *
 */
import { mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import VehicleType from './vehicle-type'

import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'
import IconLoading from '~icons/general/loading.svg'

import {
  convertExample,
  convertVrmToVehicle,
  fetchApiData,
  getDefaultVehicleType,
} from '~/helpers/garage'

export default {
  name: 'FormVrm',

  components: {
    Btn,
    IconChevronRight,
    IconLoading,
    VehicleType,
  },

  props: {
    forwarding: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: true,
      required: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    overlay: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    results: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
  },

  emits: {
    'existing-vehicle': (payload) => typeof payload === 'object',
    'vehicle-response': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      controller: false,
      error: {
        input: '',
        message: '',
      },
      formState: 'ready',
      registration: '',
      state: '',
      type: '',
      types: [],
      typeTooltipActive: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getVehicleById: 'garage/getVehicleById',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartToken: (state) => state.cart.response.token,
    }),

    /**
     * Compute local namespace.
     * @returns {String}
     */
    localNamespace() {
      return `${this.namespace}-vrm`
    },

    /**
     * Compute formatted query details.
     * @returns {Object}
     */
    query() {
      let query = this.registration.trim().replaceAll(' ', '')

      if (this.state) {
        query += `+${this.state}`
      }

      return {
        query,
        registration: this.registration,
      }
    },

    /**
     * Compute submit show state icon.
     * @returns {Boolean}
     */
    showSubmitStateIcon() {
      return this.formState === 'loading' || this.forwarding
    },

    /**
     * Compute submit CTA state icon.
     * @returns {String}
     */
    submitStateIcon() {
      return this.forwarding ? 'icon-chevron-right' : 'icon-loading'
    },
  },

  watch: {

    /**
     * Watch is active to clear form.
     */
    isActive() {
      this.resetForm()
    },

    /**
     * Watch overlay isActive to clear form.
     */
    'overlay.isActive'() {
      if (this.overlay.isActive) {
        return
      }

      this.resetForm()
    },

    /**
     * Watch results to reset form.
     */
    results() {
      if (this.results !== false) {
        return
      }

      this.resetForm()
    },
  },

  mounted() {
    this.type = getDefaultVehicleType('vrm')
  },

  methods: {

    /**
     * Reset form.
     */
    resetForm() {
      this.typeTooltipActive = false
      this.registration = ''
      this.type = getDefaultVehicleType('vrm')
      this.state = ''

      this.error = {
        input: '',
        message: '',
      }

      this.formState = 'ready'
    },

    /**
     * Handle type update.
     * @param {String} type - Vehicle type.
     */
    handleTypeUpdate(type) {
      this.type = type

      this.types = this.$variable(`vehicleFinder.types.${type}`).map((value) => {
        if (!value.includes(':')) {
          return value
        }

        return value.split(':')[1]
      })
    },

    /**
     * Handle tooltip update.
     * @param {Object} state - Tooltip state.
     */
    handleTooltipUpdate(state) {
      this.typeTooltipActive = state
    },

    /**
     * Handle form submit.
     */
    async handleSubmit() {
      try {
        this.error = {
          input: '',
          message: '',
        }

        /**
         * Check if input includes special characters, unless allowed via theme
         * setting.
         */
        const specialChars = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/
        const emojiChars = /(?<temp1>[\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g

        if (
          !this.$variable('vehicleFinder.vrmSpecialCharacters') &&
          (specialChars.test(this.registration) || emojiChars.test(this.registration))
        ) {
          this.error = {
            input: `${this.localNamespace}-registration`,
            message: this.$string('vehicle_finder.errors.special_characters'),
          }

          return
        }

        this.formState = 'loading'

        /**
         * Development mode.
         */
        if (this.$variable('settings.disableFinderFormQuery')) {
          await cnvs.Sleep(this.$timing('skeleton'))
          const vehicle = convertExample('vrm', this.type)
          this.$emit('vehicle-response', vehicle)
          return
        }

        if (this.controller.abort) {
          this.controller.abort()
        }

        this.controller = new AbortController()

        /**
         * Create unique reference for customer query.
         */
        const customerReference = this.$variable('customer.email')
          ? this.$variable('customer.email')
          : this.cartToken || new Date().getTime()

        /**
         * Query API.
         */
        const response = await fetchApiData({
          bodyData: {
            customerReference,
            registrationCountry: this.$variable('settings.regionCode'),
            registrationNumber: this.query.query,
          },
          signal: this.controller.signal,
          url: this.$variable('credentials.customApis.vrm'),
        })

        /**
         * Format vehicle.
         */
        const vehicle = convertVrmToVehicle({
          query: this.query,
          response: response.response,
          vehicleType: this.type,
        })

        /**
         * Check if vehicle already exists in garage.
         */
        if (this.getVehicleById(vehicle.id)) {
          this.$emit('existing-vehicle', vehicle)
          return
        }

        /**
         * Handle missing response or no VRM ID.
         */
        if (
          response.message !== 'found' ||
          !vehicle.details[this.$variable('vehicleFinder.handles.vrmId')]
        ) {
          this.error = {
            input: `${this.localNamespace}-registration`,
            message: this.$string('vehicle_finder.errors.not_found'),
          }

          this.formState = 'ready'
          return
        }

        /**
         * Emit response.
         */
        this.$emit('vehicle-response', vehicle)

      } catch (error) {
        this.error = {
          input: '',
          message: this.$string('vehicle_finder.errors.generic'),
        }

        this.formState = 'ready'
        cnvs.ReportError('Failed to query VRM API', error)
      }
    },
  },
}
</script>
